import type { RefObject } from "react";
import React, { useMemo } from "react";
import { Box, Heading, Table, Tbody, Tr, Td } from "@chakra-ui/react";
import { MarkdownViewer } from "screens/markdown/MarkdownViewer";
import { ShowMoreContainer } from "screens/common/components/ShowMoreContainer";
import type { DealFinderTicker } from "../../../../api/tickers/models/DealFinderTicker";

interface PurchaseDealMiddleSectionProps {
  deal: DealFinderTicker;
  middleColumnRef: RefObject<HTMLDivElement>;
  middleScrollbarStyle: any; // Using any to avoid type issues with css prop
  textColor: string;
  bgColor: string;
  borderColor: string;
}

export const PurchaseDealMiddleSection: React.FC<PurchaseDealMiddleSectionProps> = ({
  deal,
  middleColumnRef,
  middleScrollbarStyle,
  textColor,
  bgColor,
  borderColor,
}) => {
  const companyDescription =
    deal.companyDescription ||
    `~~Mock Data Do Not Use. ${deal.name} is a private company that operates in the professional, scientific, and technical services sector, specifically in the management consulting services industry. The company was founded in 2011 and is headquartered in Portland, Oregon. ${deal.name} provides services that help organizations verify the identity of their customers, employees, or students, ensuring that they are who they claim to be. The company's solutions are designed to prevent fraud, improve customer experience, and reduce costs associated with manual verification processes. With a valuation of $XX million, ${deal.name} has established itself as a player in the identity verification market, serving clients across various industries.~~`;

  const cleanMarkdown = (text: string) => {
    // TODO: Remove this when the header is no longer being sent in the markdown
    const lines = text.split("\n").filter((line) => line.trim() !== "");
    if (lines[0]?.trim().startsWith("**") && lines[0]?.trim().endsWith("**")) {
      return lines.slice(1).join("\n").trim();
    }
    return text;
  };

  const investmentPotentialDescription = useMemo(() => {
    const rawDescription =
      deal.investmentPotential ||
      `~~Mock Data Do Not Use. ${deal.name}'s investment potential is moderate, with a revenue growth rate of XX% quarterly YoY and XX% YoY. The company's gross profit margin is 42.1%, indicating a relatively low margin business. However, the company's asset turnover ratio is 0.762, suggesting efficient use of assets. The net profit margin is -XX%, indicating the company is currently operating at a loss. With a valuation of $XX million, investors should carefully consider the company's growth prospects and financial performance before investing.}~~`;

    return cleanMarkdown(rawDescription);
  }, [deal.investmentPotential, deal.name]);

  // Generate memoized random values for financial fundamentals when useMockData is true
  const mockFinancialValues = useMemo(() => {
    // Helper function to get a random value with specified format
    const getRandomValue = (key: string): string => {
      const keyLower = key.toLowerCase();

      if (keyLower.includes("rate") || keyLower.includes("margin") || keyLower.includes("percent") || keyLower.includes("ratio")) {
        const value = (Math.random() * 65 - 20).toFixed(2);
        return `~~${value}~~%`;
      } else if (keyLower.includes("revenue") || keyLower.includes("profit") || keyLower.includes("income") || keyLower.includes("sales")) {
        const numValue = Math.random() * 550 - 50;
        const formattedValue = numValue.toFixed(1);
        return `$~~${Math.abs(numValue) < 100 ? formattedValue + "M" : (numValue / 1000).toFixed(2) + "B"}~~`;
      } else if (keyLower.includes("p/e") || keyLower.includes("ev/") || keyLower.includes("price")) {
        return `~~${(Math.random() * 48 + 2).toFixed(1)}~~`;
      } else {
        const isNegative = Math.random() < 0.2;
        const baseValue = Math.random() * 1000 + 10;
        const value = isNegative ? -baseValue : baseValue;
        return `~~${Math.random() < 0.5 ? value.toFixed(2) : Math.floor(value).toString()}~~`;
      }
    };

    // If financialFundamentals is null, create mock data with common financial metrics
    const defaultMetrics = [
      "Revenue Growth Rate",
      "Gross Profit Margin",
      "Net Profit Margin",
      "Return on Equity",
      "Debt to Equity Ratio",
      "P/E Ratio",
      "EV/EBITDA",
      "Annual Revenue",
    ];

    return defaultMetrics.reduce((acc, key) => {
      acc[key] = getRandomValue(key);
      return acc;
    }, {} as Record<string, string>);
  }, []);

  // Create mock financialFundamentals
  const mockFinancialFundamentalsObject = useMemo(() => {
    return Object.keys(mockFinancialValues).reduce((acc, key) => {
      acc[key] = {
        humanReadableValue: mockFinancialValues[key],
        value: 0,
      };
      return acc;
    }, {} as typeof deal.financialFundamentals);
  }, [deal, mockFinancialValues]);

  const areFinancialFundamentalsValuesNull = useMemo(() => {
    return deal.financialFundamentals && Object.values(deal.financialFundamentals).every((item) => (item as any).rawValue === null);
  }, [deal.financialFundamentals]);

  const financialFundamentalsToDisplay = useMemo(() => {
    const fundamentals = areFinancialFundamentalsValuesNull
      ? mockFinancialFundamentalsObject
      : deal.financialFundamentals || mockFinancialFundamentalsObject;

    if (!fundamentals) return {};

    return fundamentals;
  }, [areFinancialFundamentalsValuesNull, deal.financialFundamentals, mockFinancialFundamentalsObject]);

  return (
    <Box
      width="100%"
      height="100%"
      color={textColor}
      py="1.5rem"
      pr={{ base: "1rem", lg: "1.5rem" }}
      pl={{ base: "1rem", lg: ".75rem" }}
      overflowY="auto"
      css={middleScrollbarStyle}
      ref={middleColumnRef}>
      <Box mb="1rem">
        <Heading size="md" mb={3}>
          Business Description
        </Heading>
        <ShowMoreContainer maxHeight="14rem" minHeight="7rem" scrollingContainer>
          <MarkdownViewer
            noBoldHeadings
            blurNumbers
            backgroundColor={bgColor}
            fontColor={textColor}
            content={companyDescription}
            fontSize="sm"
          />
        </ShowMoreContainer>
      </Box>
      <Box mb="1rem">
        <Heading size="md" mb={3}>
          Latest Fundamentals
        </Heading>
        <Box mb={4} borderWidth="1px" borderColor={borderColor} borderRadius="md" overflow="hidden">
          <Table size="sm" variant="simple">
            <Tbody>
              {financialFundamentalsToDisplay &&
                Object.entries(financialFundamentalsToDisplay)
                  // Filter out rows where humanReadableValue is null (unless using mock data)
                  .filter(
                    ([_, score]) =>
                      !deal.financialFundamentals || (score as { humanReadableValue: string | null }).humanReadableValue !== null
                  )
                  .map(([scoreType, score]) => (
                    <Tr key={scoreType}>
                      <Td fontSize="sm" fontWeight="medium" py={2}>
                        {scoreType}
                      </Td>
                      <Td isNumeric fontSize="sm" color="blue.600" py={2}>
                        {(() => {
                          const value = !deal.financialFundamentals
                            ? mockFinancialValues[scoreType]
                            : (score as { humanReadableValue: string | null }).humanReadableValue;

                          // Check if value contains ~~ wrapped content
                          if (typeof value === "string" && value.includes("~~")) {
                            const regex = /~~([^~]+)~~/g;
                            const parts: (string | React.ReactElement)[] = [];
                            let lastIndex = 0;
                            let match;

                            while ((match = regex.exec(value)) !== null) {
                              // Add text before the match (if any)
                              if (match.index > lastIndex) {
                                parts.push(value.slice(lastIndex, match.index));
                              }

                              // Add the blurred content
                              parts.push(
                                <Box key={match.index} as="span" filter="blur(3px)" cursor="pointer">
                                  {match[1]}
                                </Box>
                              );

                              lastIndex = regex.lastIndex;
                            }

                            // Add any remaining text after the last match
                            if (lastIndex < value.length) {
                              parts.push(value.slice(lastIndex));
                            }

                            return <>{parts}</>;
                          }
                          return value;
                        })()}
                      </Td>
                    </Tr>
                  ))}
            </Tbody>
          </Table>
        </Box>
      </Box>
      <Box mb="1rem">
        <Heading size="md" mb={3}>
          Investment Potential
        </Heading>
        <ShowMoreContainer maxHeight="14rem" minHeight="8rem" scrollingContainer>
          <MarkdownViewer
            noBoldHeadings
            blurNumbers
            backgroundColor={bgColor}
            fontColor={textColor}
            content={investmentPotentialDescription}
            fontSize="sm"
          />
        </ShowMoreContainer>
      </Box>
    </Box>
  );
};
